import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { apiGet } from '../../utils/apiClient';
import SummonerHeader from './components/SummonerHeader';
import RankedInfo from './components/RankedInfo';
import MatchCard from './components/MatchCard';
import SummonerSearchBar from './components/SummonerSearchBar';
import ChampionStats from './components/ChampionStats';
import './SummonerPage.css';

interface SummonerData {
  account: {
    puuid: string;
    gameName: string;
    tagLine: string;
  };
  summoner: {
    profileIconId: number;
    summonerLevel: number;
  };
  ranked: Array<{
    queueType: string;
    tier: string;
    rank: string;
    leaguePoints: number;
    wins: number;
    losses: number;
  }>;
  cached?: boolean;
}

interface MatchData {
  metadata: {
    matchId: string;
    participants: string[];
  };
  info: {
    gameCreation: number;
    gameDuration: number;
    gameMode: string;
    queueId: number;
    participants: Array<any>;
  };
}

const SummonerPage: React.FC = () => {
  const { region, gameName, tagLine } = useParams<{
    region: string;
    gameName: string;
    tagLine: string;
  }>();
  const navigate = useNavigate();

  const [summoner, setSummoner] = useState<SummonerData | null>(null);
  const [matches, setMatches] = useState<MatchData[]>([]);
  const [loading, setLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitMsg, setRateLimitMsg] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [ddragonVersion, setDdragonVersion] = useState<string>('14.10.1');

  useEffect(() => {
    fetch('https://ddragon.leagueoflegends.com/api/versions.json')
      .then((res) => res.json())
      .then((versions: string[]) => setDdragonVersion(versions[0]))
      .catch(() => {});
  }, []);

  const fetchData = useCallback((refresh: boolean) => {
    if (!region || !gameName || !tagLine) return;

    const refreshParam = refresh ? '?refresh=true' : '';

    if (!refresh) {
      setLoading(true);
      setError(null);
      setSummoner(null);
      setMatches([]);
      setMatchesLoading(true);
      setHasMore(true);
    } else {
      setUpdating(true);
      setRateLimitMsg(null);
    }

    const encodedName = encodeURIComponent(gameName);
    const encodedTag = encodeURIComponent(tagLine);

    apiGet(`/api/summoner/${region}/${encodedName}/${encodedTag}${refreshParam}`)
      .then((data: SummonerData) => {
        setSummoner(data);
        setLoading(false);

        const matchRefresh = refresh ? '&refresh=true' : '';
        apiGet(`/api/summoner/${region}/matches/${data.account.puuid}?count=20${matchRefresh}`)
          .then((matchData: MatchData[]) => {
            setMatches(matchData);
            setMatchesLoading(false);
            setUpdating(false);
          })
          .catch((err) => {
            if (err.message?.includes('429')) {
              setRateLimitMsg('Rate limited — try again in a few seconds');
            }
            setMatchesLoading(false);
            setUpdating(false);
          });
      })
      .catch((err) => {
        if (err.message?.includes('429')) {
          setRateLimitMsg('Rate limited — try again in a few seconds');
          setUpdating(false);
          return;
        }
        setError(err.message || 'Failed to load summoner');
        setLoading(false);
        setMatchesLoading(false);
        setUpdating(false);
      });
  }, [region, gameName, tagLine]);

  // Initial load (from cache)
  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const handleUpdate = () => {
    if (updating) return;
    fetchData(true);
  };

  const handleSearch = (searchRegion: string, searchName: string, searchTag: string) => {
    navigate(`/summoner/${searchRegion}/${encodeURIComponent(searchName)}/${encodeURIComponent(searchTag)}`);
  };

  const handleLoadMore = () => {
    if (loadingMore || !summoner || !region) return;
    setLoadingMore(true);
    setRateLimitMsg(null);

    const start = matches.length;
    apiGet(`/api/summoner/${region}/matches/${summoner.account.puuid}?count=20&start=${start}&refresh=true`)
      .then((newMatches: MatchData[]) => {
        if (newMatches.length === 0) {
          setHasMore(false);
        } else {
          setMatches((prev) => [...prev, ...newMatches]);
        }
        setLoadingMore(false);
      })
      .catch((err) => {
        if (err.message?.includes('429')) {
          setRateLimitMsg('Rate limited — try again in a few seconds');
        }
        setLoadingMore(false);
      });
  };

  if (loading) {
    return (
      <div className="summoner-page">
        <div className="summoner-loading">
          <div className="loading-spinner" />
          <p>Looking up {gameName}#{tagLine}...</p>
        </div>
      </div>
    );
  }

  if (error || !summoner) {
    return (
      <div className="summoner-page">
        <div className="summoner-error">
          <h2>Summoner not found</h2>
          <p>{error || `Could not find ${gameName}#${tagLine} on ${region?.toUpperCase()}`}</p>
          <SummonerSearchBar onSearch={handleSearch} defaultRegion={region || 'euw'} />
        </div>
      </div>
    );
  }

  const soloQueue = summoner.ranked.find((r) => r.queueType === 'RANKED_SOLO_5x5');
  const flexQueue = summoner.ranked.find((r) => r.queueType === 'RANKED_FLEX_SR');

  return (
    <div className="summoner-page">
      <Helmet>
        <title>{summoner.account.gameName}#{summoner.account.tagLine} - LoL Calculator</title>
      </Helmet>

      <SummonerHeader
        gameName={summoner.account.gameName}
        tagLine={summoner.account.tagLine}
        profileIconId={summoner.summoner.profileIconId}
        summonerLevel={summoner.summoner.summonerLevel}
        region={region!}
        ddragonVersion={ddragonVersion}
        onUpdate={handleUpdate}
        updating={updating}
        cached={summoner.cached}
      />

      {rateLimitMsg && (
        <div className="rate-limit-banner">{rateLimitMsg}</div>
      )}

      <div className="summoner-content">
        <aside className="summoner-sidebar">
          <RankedInfo label="Ranked Solo" data={soloQueue} />
          <RankedInfo label="Ranked Flex" data={flexQueue} />
          <ChampionStats matches={matches} puuid={summoner.account.puuid} ddragonVersion={ddragonVersion} />
          <SummonerSearchBar onSearch={handleSearch} defaultRegion={region || 'euw'} compact />
        </aside>

        <main className="summoner-main">
          <h2 className="match-history-title">Match History</h2>

          {matchesLoading ? (
            <div className="matches-loading">
              <div className="loading-spinner" />
              <p>Loading matches...</p>
            </div>
          ) : matches.length === 0 ? (
            <p className="no-matches">No recent matches found.</p>
          ) : (
            <>
              <div className="match-list">
                {matches.map((match) => (
                  <MatchCard
                    key={match.metadata.matchId}
                    match={match}
                    puuid={summoner.account.puuid}
                    ddragonVersion={ddragonVersion}
                  />
                ))}
              </div>
              {hasMore && (
                <button
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default SummonerPage;
